import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { graphql, useFragment, useMutation } from 'react-relay';
import type { TopicList_topic$key } from '@/__generated__/TopicList_topic.graphql';
import type { TopicListSetActiveMutation } from '@/__generated__/TopicListSetActiveMutation.graphql';
import type { TopicListDeleteMutation } from '@/__generated__/TopicListDeleteMutation.graphql';
import type { TopicListTriggerIngestionMutation } from '@/__generated__/TopicListTriggerIngestionMutation.graphql';

/**
 * Fragment for topic data needed by TopicList
 */
const TopicList_topic = graphql`
  fragment TopicList_topic on Topic {
    id
    slug
    name
    isActive
    visibility
    status
    episodeCount
    lastIngestionAt
    lastExecutionStatus
    lastExecutionStartedAt
    canTriggerIngestion: canAct(action: TRIGGER_INGESTION)
  }
`;

/**
 * Mutation to set topic active status
 */
const setTopicActiveMutation = graphql`
  mutation TopicListSetActiveMutation($input: SetTopicActiveInput!) {
    setTopicActive(input: $input) {
      topic {
        id
        isActive
        canTriggerIngestion: canAct(action: TRIGGER_INGESTION)
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

/**
 * Mutation to delete a topic
 */
const deleteTopicMutation = graphql`
  mutation TopicListDeleteMutation($input: DeleteTopicInput!, $connections: [ID!]!) {
    deleteTopic(input: $input) {
      deletedTopicId @deleteEdge(connections: $connections)
      success
      userErrors {
        field
        message
        code
      }
    }
  }
`;

/**
 * Mutation to trigger ingestion
 */
const triggerIngestionMutation = graphql`
  mutation TopicListTriggerIngestionMutation($input: TriggerIngestionInput!) {
    triggerIngestion(input: $input) {
      success
      message
      userErrors {
        field
        message
        code
      }
    }
  }
`;

const VISIBILITY_STYLES: Record<string, { bg: string; text: string; ring: string; label: string }> = {
  PUBLIC: { bg: 'bg-blue-500/20', text: 'text-blue-400', ring: 'ring-blue-500/30', label: 'Public' },
  UNLISTED: { bg: 'bg-amber-500/20', text: 'text-amber-400', ring: 'ring-amber-500/30', label: 'Unlisted' },
  PRIVATE: { bg: 'bg-purple-500/20', text: 'text-purple-400', ring: 'ring-purple-500/30', label: 'Private' },
};

const EXECUTION_STATUS_STYLES: Record<string, { bg: string; text: string; ring: string; label: string; icon?: 'spinner' | 'check' | 'x' }> = {
  RUNNING: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', ring: 'ring-cyan-500/30', label: 'Running', icon: 'spinner' },
  SUCCEEDED: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', ring: 'ring-emerald-500/30', label: 'Succeeded', icon: 'check' },
  FAILED: { bg: 'bg-red-500/20', text: 'text-red-400', ring: 'ring-red-500/30', label: 'Failed', icon: 'x' },
  TIMED_OUT: { bg: 'bg-orange-500/20', text: 'text-orange-400', ring: 'ring-orange-500/30', label: 'Timed Out', icon: 'x' },
  ABORTED: { bg: 'bg-gray-500/20', text: 'text-gray-400', ring: 'ring-gray-500/30', label: 'Aborted', icon: 'x' },
  PENDING_REDRIVE: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', ring: 'ring-yellow-500/30', label: 'Pending', icon: 'spinner' },
};

interface TopicItem {
  id: string;
  fragmentRef: TopicList_topic$key;
}

interface TopicListProps {
  topics: ReadonlyArray<TopicItem>;
  connectionId?: string;
}

interface IngestionResult {
  topicSlug: string;
  success: boolean;
  message: string | null | undefined;
}

function formatLastIngestion(date: string | null | undefined): string {
  if (!date) return 'Never';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Less than an hour ago';
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString();
}

function formatExecutionTime(date: string | null | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleString();
}

interface TopicRowProps {
  topicRef: TopicList_topic$key;
  connectionId?: string;
  openDropdown: string | null;
  setOpenDropdown: (slug: string | null) => void;
  deleteConfirm: string | null;
  setDeleteConfirm: (slug: string | null) => void;
  ingestionResult: IngestionResult | null;
  setIngestionResult: (result: IngestionResult | null) => void;
  ingestionInProgress: string | null;
  setIngestionInProgress: (slug: string | null) => void;
}

function TopicRow({
  topicRef,
  connectionId,
  openDropdown,
  setOpenDropdown,
  deleteConfirm,
  setDeleteConfirm,
  ingestionResult,
  setIngestionResult,
  ingestionInProgress,
  setIngestionInProgress,
}: TopicRowProps) {
  const topic = useFragment(TopicList_topic, topicRef);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [commitSetActive, isSettingActive] = useMutation<TopicListSetActiveMutation>(setTopicActiveMutation);
  const [commitDelete, isDeleting] = useMutation<TopicListDeleteMutation>(deleteTopicMutation);
  const [commitTriggerIngestion] = useMutation<TopicListTriggerIngestionMutation>(triggerIngestionMutation);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    }
    if (openDropdown === topic.slug) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdown, topic.slug, setOpenDropdown]);

  const handleToggleActive = () => {
    commitSetActive({
      variables: {
        input: {
          slug: topic.slug,
          isActive: !topic.isActive,
        },
      },
      optimisticResponse: {
        setTopicActive: {
          topic: {
            id: topic.id,
            isActive: !topic.isActive,
            canTriggerIngestion: !topic.isActive,
          },
          userErrors: [],
        },
      },
    });
    setOpenDropdown(null);
  };

  const handleDelete = () => {
    commitDelete({
      variables: {
        input: { slug: topic.slug },
        connections: connectionId ? [connectionId] : [],
      },
      onCompleted: () => {
        setDeleteConfirm(null);
      },
    });
  };

  const handleTriggerIngestion = () => {
    setIngestionInProgress(topic.slug);
    setIngestionResult(null);
    setOpenDropdown(null);

    commitTriggerIngestion({
      variables: {
        input: { topicSlug: topic.slug },
      },
      onCompleted: (response) => {
        setIngestionResult({
          topicSlug: topic.slug,
          success: response.triggerIngestion.success,
          message: response.triggerIngestion.message,
        });
        setIngestionInProgress(null);
      },
      onError: () => {
        setIngestionResult({
          topicSlug: topic.slug,
          success: false,
          message: 'Failed to trigger ingestion',
        });
        setIngestionInProgress(null);
      },
    });
  };

  return (
    <div className={`card group transition-all hover:border-gray-700 relative ${openDropdown === topic.slug ? 'z-20' : 'z-0'}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 ring-1 ring-white/10">
              <svg className="h-5 w-5 text-indigo-400" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3C7.03 3 3 7.03 3 12v7c0 1.1.9 2 2 2h2v-8H5v-1c0-3.87 3.13-7 7-7s7 3.13 7 7v1h-2v8h2c1.1 0 2-.9 2-2v-7c0-4.97-4.03-9-9-9zM7 15v4H5v-4h2zm12 4h-2v-4h2v4z"/>
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-lg font-semibold text-white">
                  {topic.name}
                </h3>
                <a
                  href={`/p/${topic.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 text-gray-400 hover:text-indigo-400 transition-colors"
                  title={`View /p/${topic.slug}`}
                >
                  <span className="sr-only">View {topic.name} topic page</span>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    topic.isActive
                      ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30'
                      : 'bg-gray-700/50 text-gray-400 ring-1 ring-gray-600'
                  }`}
                >
                  {topic.isActive ? 'Active' : 'Inactive'}
                </span>
                {topic.visibility && topic.visibility !== 'PUBLIC' && (
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${
                      VISIBILITY_STYLES[topic.visibility]?.bg ?? ''
                    } ${VISIBILITY_STYLES[topic.visibility]?.text ?? ''} ${
                      VISIBILITY_STYLES[topic.visibility]?.ring ?? ''
                    }`}
                  >
                    {VISIBILITY_STYLES[topic.visibility]?.label ?? topic.visibility}
                  </span>
                )}
                {topic.lastExecutionStatus && EXECUTION_STATUS_STYLES[topic.lastExecutionStatus] && (
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 flex items-center gap-1 ${
                      EXECUTION_STATUS_STYLES[topic.lastExecutionStatus].bg
                    } ${EXECUTION_STATUS_STYLES[topic.lastExecutionStatus].text} ${
                      EXECUTION_STATUS_STYLES[topic.lastExecutionStatus].ring
                    }`}
                    title={`Last execution: ${formatExecutionTime(topic.lastExecutionStartedAt)}`}
                  >
                    {EXECUTION_STATUS_STYLES[topic.lastExecutionStatus].icon === 'spinner' && (
                      <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {EXECUTION_STATUS_STYLES[topic.lastExecutionStatus].icon === 'check' && (
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {EXECUTION_STATUS_STYLES[topic.lastExecutionStatus].icon === 'x' && (
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    {EXECUTION_STATUS_STYLES[topic.lastExecutionStatus].label}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-2 pl-[52px]">
            <span className="text-xs text-gray-500">
              Last ingested: {formatLastIngestion(topic.lastIngestionAt)}
            </span>
          </div>
        </div>

        {/* Actions Dropdown */}
        <div className="relative flex-shrink-0 self-start sm:self-auto" ref={openDropdown === topic.slug ? dropdownRef : null}>
          <button
            type="button"
            onClick={() => setOpenDropdown(openDropdown === topic.slug ? null : topic.slug)}
            className="btn-secondary flex items-center gap-2"
            aria-haspopup="true"
            aria-expanded={openDropdown === topic.slug}
          >
            Actions
            <svg className={`h-4 w-4 transition-transform ${openDropdown === topic.slug ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {openDropdown === topic.slug && (
            <div className="absolute left-0 sm:left-auto sm:right-0 z-10 mt-2 w-48 origin-top-left sm:origin-top-right rounded-xl border border-gray-700 bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="py-1" role="menu" aria-orientation="vertical">
                {/* Ingest Action */}
                <button
                  type="button"
                  onClick={handleTriggerIngestion}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!topic.canTriggerIngestion || ingestionInProgress === topic.slug || topic.status === 'RUNNING'}
                  role="menuitem"
                  title={
                    !topic.isActive
                      ? 'Activate topic to trigger ingestion'
                      : topic.status === 'RUNNING'
                        ? 'Ingestion is currently running'
                        : !topic.canTriggerIngestion
                          ? 'Ingestion on cooldown (4 hours between runs)'
                          : 'Trigger content ingestion'
                  }
                >
                  {ingestionInProgress === topic.slug || topic.status === 'RUNNING' ? (
                    <svg className="h-4 w-4 animate-spin text-indigo-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                  {ingestionInProgress === topic.slug || topic.status === 'RUNNING'
                    ? (topic.status === 'RUNNING' ? 'Running...' : 'Processing...')
                    : 'Ingest'}
                </button>

                {/* Activate/Deactivate Action */}
                <button
                  type="button"
                  onClick={handleToggleActive}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSettingActive}
                  role="menuitem"
                >
                  {topic.isActive ? (
                    <svg className="h-4 w-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {topic.isActive ? 'Deactivate' : 'Activate'}
                </button>

                {/* Edit Action */}
                <Link
                  to={`/admin/topics/${topic.slug}/edit`}
                  onClick={() => setOpenDropdown(null)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white"
                  role="menuitem"
                >
                  <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </Link>

                {/* View Episodes Action */}
                <Link
                  to={`/admin/episodes/${topic.slug}`}
                  onClick={() => setOpenDropdown(null)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white"
                  role="menuitem"
                >
                  <svg className="h-4 w-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                  </svg>
                  View Episodes
                  {topic.episodeCount > 0 && (
                    <span className="ml-auto text-xs text-gray-500">
                      {topic.episodeCount}
                    </span>
                  )}
                </Link>

                {/* Divider */}
                <div className="my-1 border-t border-gray-700" />

                {/* Delete Action */}
                <button
                  type="button"
                  onClick={() => {
                    setDeleteConfirm(topic.slug);
                    setOpenDropdown(null);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  role="menuitem"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirm === topic.slug && (
        <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
          <p className="text-sm text-red-200">
            Are you sure you want to delete <strong className="text-white">{topic.name}</strong>? This action cannot be undone.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handleDelete}
              className="btn-danger"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button
              type="button"
              onClick={() => setDeleteConfirm(null)}
              className="btn-secondary"
              disabled={isDeleting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Ingestion result notification */}
      {ingestionResult && ingestionResult.topicSlug === topic.slug && (
        <div
          className={`mt-4 rounded-xl border p-4 ${
            ingestionResult.success
              ? 'border-blue-500/30 bg-blue-500/10'
              : 'border-amber-500/30 bg-amber-500/10'
          }`}
        >
          <div className="flex items-start justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  ingestionResult.success ? 'text-blue-400' : 'text-amber-400'
                }`}
              >
                {ingestionResult.success ? 'Ingestion Started' : 'Ingestion Issue'}
              </p>
              <p className="mt-1 text-sm text-gray-300">{ingestionResult.message ?? ''}</p>
            </div>
            <button
              type="button"
              onClick={() => setIngestionResult(null)}
              className="text-gray-400 hover:text-white"
              aria-label="Dismiss notification"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface TopicRowWithFilterProps {
  topicRef: TopicList_topic$key;
  connectionId?: string;
  searchQuery: string;
  openDropdown: string | null;
  setOpenDropdown: (slug: string | null) => void;
  deleteConfirm: string | null;
  setDeleteConfirm: (slug: string | null) => void;
  ingestionResult: IngestionResult | null;
  setIngestionResult: (result: IngestionResult | null) => void;
  ingestionInProgress: string | null;
  setIngestionInProgress: (slug: string | null) => void;
}

function TopicRowWithFilter({
  topicRef,
  searchQuery,
  ...props
}: TopicRowWithFilterProps) {
  const topic = useFragment(TopicList_topic, topicRef);

  // Filter by search query
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    const matches =
      topic.name.toLowerCase().includes(query) ||
      topic.slug.toLowerCase().includes(query);
    if (!matches) {
      return null;
    }
  }

  return <TopicRow topicRef={topicRef} {...props} />;
}

export function TopicList({ topics, connectionId }: TopicListProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [ingestionResult, setIngestionResult] = useState<IngestionResult | null>(null);
  const [ingestionInProgress, setIngestionInProgress] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  if (topics.length === 0) {
    return (
      <div className="card text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-800">
          <svg className="h-7 w-7 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-white">No topics yet</h3>
        <p className="mt-1 text-gray-400">Create your first podcast topic to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search topics..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input w-full max-w-sm pl-10"
        />
      </div>

      {topics.map((topic) => (
        <TopicRowWithFilter
          key={topic.id}
          topicRef={topic.fragmentRef}
          connectionId={connectionId}
          searchQuery={searchQuery}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          deleteConfirm={deleteConfirm}
          setDeleteConfirm={setDeleteConfirm}
          ingestionResult={ingestionResult}
          setIngestionResult={setIngestionResult}
          ingestionInProgress={ingestionInProgress}
          setIngestionInProgress={setIngestionInProgress}
        />
      ))}
    </div>
  );
}
